import { Button, HintLabel, TextField } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import type { ReactNode } from 'react';
import {
  aliasCount,
  aliasedUnits,
  cartonCount,
  type InventoryItem,
  type InventoryVariant,
  latestVariant,
  type QuantityView,
} from '../../lib/inventory';
import { PermissionGate } from '../PermissionGate';
import type { InventoryRowContext } from './InventoryRows';
import { StockOutlookText } from './StockOutlookText';

const count = (value?: number | string) =>
  CommonUtility.numberWithCommas(value || 0);

function Stack({ children }: { children: ReactNode }) {
  return (
    <div className="sk:flex sk:flex-col sk:items-start sk:gap-1">
      {children}
    </div>
  );
}

function Note({ children }: { children: ReactNode }) {
  return (
    <span className="sk:flex sk:flex-wrap sk:items-center sk:gap-x-1 sk:text-xs sk:text-fg-tertiary">
      {children}
    </span>
  );
}

/** A quantity typed straight into the row. */
function QuantityInput({
  label,
  value,
  onChange,
  isDisabled,
  context,
  suffix,
}: {
  label: string;
  value?: number;
  onChange: (value: string) => void;
  isDisabled: boolean;
  context: InventoryRowContext;
  suffix?: string;
}) {
  return (
    <PermissionGate
      disabled={context.permission.disabled}
      hidden={context.permission.hidden}
    >
      <span className="sk:flex sk:items-center sk:gap-1.5">
        <TextField
          size="sm"
          label={label}
          type="number"
          value={
            value === undefined || Number.isNaN(value) ? '' : String(value)
          }
          onChange={onChange}
          isDisabled={isDisabled || context.permission.disabled}
          className="sk:w-20"
        />
        {suffix ? (
          <span className="sk:text-dense sk:text-fg-tertiary">{suffix}</span>
        ) : null}
      </span>
    </PermissionGate>
  );
}

function Outlook({
  item,
  view,
  context,
}: {
  item: InventoryItem;
  view: QuantityView;
  context: InventoryRowContext;
}) {
  return (
    <StockOutlookText
      item={item}
      onHand={view.onHand}
      isProcessing={
        context.onceSubmitted && context.processing.has(item.item_id)
      }
      isFBA={context.flags.isFBA}
    />
  );
}

/** Units per FBA SKU behind the item's figure. */
function SkuSplit({ item }: { item: InventoryItem }) {
  return (
    <HintLabel
      hint={
        <span className="sk:flex sk:min-w-60 sk:flex-col sk:gap-1">
          <span className="sk:flex sk:justify-between sk:gap-4 sk:opacity-70">
            <span>Availability by FBA SKU</span>
            <span>Units Available</span>
          </span>
          {item.inventory_by_sku?.map(sku => (
            <span key={sku.sku} className="sk:flex sk:justify-between sk:gap-4">
              <span>{sku.sku}</span>
              <span className="sk:tabular-nums">{sku.available}</span>
            </span>
          ))}
          <span className="sk:flex sk:justify-between sk:gap-4">
            <span className="sk:opacity-70">Total</span>
            <span className="sk:tabular-nums">{count(item.available)}</span>
          </span>
        </span>
      }
    >
      <span className="sk:tabular-nums">{count(item.available)}</span>
    </HintLabel>
  );
}

/**
 * The item's cartons: an input on a manual or hybrid provider, a figure where
 * the provider reports stock itself.
 */
export function CartonsPart({
  item,
  view,
  context,
}: {
  item: InventoryItem;
  view: QuantityView;
  context: InventoryRowContext;
}) {
  const { flags, readOnly } = context;
  const latest = latestVariant(item).attribute;
  const outlook = view.showsLoose ? null : (
    <Outlook item={item} view={view} context={context} />
  );

  if (flags.isManual || (flags.isFulfillable && flags.isWarehouse)) {
    if (aliasCount(item, latest) > 1) {
      return (
        <Stack>
          <span className="sk:text-fg-secondary">Multiple</span>
          {outlook ? <Note>{outlook}</Note> : null}
        </Stack>
      );
    }

    if (!latest.alias || !view.showsCartonInput) return null;

    const cartons = context.cartons[item.item_id]?.available;

    return (
      <Stack>
        <QuantityInput
          label={`Cartons of ${item.item_sku}`}
          value={cartons}
          onChange={value => context.onCartonsChange(item.item_id, value)}
          isDisabled={readOnly}
          context={context}
        />
        {outlook ? (
          <Note>
            <span>
              {count(
                Math.round((cartons ?? 0) * (latest.units_per_carton || 1)),
              )}{' '}
              Units
            </span>
            {outlook}
          </Note>
        ) : null}
      </Stack>
    );
  }

  if (context.displaySetting === 'unit') {
    return (
      <Stack>
        {flags.isFBA ? (
          <SkuSplit item={item} />
        ) : (
          <span className="sk:tabular-nums">{count(item.available)}</span>
        )}
        {outlook ? <Note>{outlook}</Note> : null}
      </Stack>
    );
  }

  const cartons = cartonCount(item);

  return (
    <Stack>
      <span className="sk:tabular-nums">
        {cartons ? count(CommonUtility.roundNumber(cartons)) : '0'}
      </span>
      {outlook ? (
        <Note>
          <span>{count(aliasedUnits(item))} Units</span>
          {outlook}
        </Note>
      ) : null}
    </Stack>
  );
}

/** Units under no alias: typed in on a manual provider, shown elsewhere. */
export function LoosePart({
  item,
  view,
  context,
}: {
  item: InventoryItem;
  view: QuantityView;
  context: InventoryRowContext;
}) {
  if (!view.showsLoose) return null;

  const value = context.loose[item.item_id]?.newValue;
  const unit = item.unit_of_measurement;
  const suffix = unit && unit.toLowerCase() !== 'units' ? unit : undefined;

  return (
    <Stack>
      {context.pureFulfillable ? (
        <span className="sk:tabular-nums">{value}</span>
      ) : (
        <QuantityInput
          label={`Loose units of ${item.item_sku}`}
          value={value}
          onChange={next => context.onLooseChange(item.item_id, next)}
          isDisabled={context.readOnly || !context.flags.isManual}
          context={context}
          suffix={suffix}
        />
      )}
      {view.showsTotal ? null : (
        <Note>
          <Outlook item={item} view={view} context={context} />
        </Note>
      )}
    </Stack>
  );
}

/** All the item's units and how long they last, once cartons and loose units both show. */
export function QuantityTotal({
  item,
  view,
  context,
}: {
  item: InventoryItem;
  view: QuantityView;
  context: InventoryRowContext;
}) {
  if (!view.showsTotal) return null;

  return (
    <Note>
      <span>{count(view.onHand)} Units</span>
      <Outlook item={item} view={view} context={context} />
    </Note>
  );
}

/** One alias's cartons, under an item with several. */
export function VariantQuantity({
  item,
  variant,
  index,
  context,
}: {
  item: InventoryItem;
  variant: InventoryVariant;
  index: number;
  context: InventoryRowContext;
}) {
  const cartons = context.cartons[item.item_id]?.byVariant[variant._id];
  const hasCarton = CommonUtility.isValidObject(variant.attribute ?? {});
  let note: ReactNode = (
    <Note>
      {count(
        Math.round((cartons || 0) * (variant.attribute?.units_per_carton || 1)),
      )}{' '}
      Units
    </Note>
  );

  if (!hasCarton) {
    note = context.flags.isWarehouse ? (
      <Note>{variant.available} Units</Note>
    ) : (
      <MissingProperties onPress={() => context.onEdit(item, index)} />
    );
  }

  return (
    <Stack>
      {context.isApiFtp || context.isLogisticsPlus ? (
        <span className="sk:tabular-nums">{cartons || 0}</span>
      ) : hasCarton ? (
        <QuantityInput
          label={`Cartons of ${variant.alias}`}
          value={cartons}
          onChange={value =>
            context.onCartonsChange(item.item_id, value, variant._id)
          }
          isDisabled={context.readOnly}
          context={context}
        />
      ) : null}
      {note}
    </Stack>
  );
}

/** The carton set-up is incomplete; pressing it opens the set-up. */
export function MissingProperties({
  onPress,
  isDisabled,
}: {
  onPress: () => void;
  isDisabled?: boolean;
}) {
  return (
    <Button
      variant="danger"
      size="sm"
      onPress={onPress}
      isDisabled={isDisabled}
    >
      Missing Properties
    </Button>
  );
}
